import { useState, useEffect } from 'react';

import { SelectOption } from '~/shared/utils/types';
import { useFilterUrlParams } from './useFilterUrlParams';

interface UseFilterStateProps {
  open: boolean;
  appliedClusterNameFilters: SelectOption[];
  appliedInstanceFilters: SelectOption[];
  appliedVolumeIdFilters: SelectOption[];
  appliedVolumeTagFilters: SelectOption[];
  appliedAccountFilters: SelectOption[];
  appliedAutoscaleState: string[];
  appliedEligibility: string[];
}

export function useFilterState({
  open,
  appliedClusterNameFilters,
  appliedInstanceFilters,
  appliedVolumeIdFilters,
  appliedVolumeTagFilters,
  appliedAccountFilters,
  appliedAutoscaleState,
  appliedEligibility,
}: UseFilterStateProps) {
  const { applyFilters } = useFilterUrlParams();
  const [pendingAutoscaleState, setPendingAutoscaleState] =
    useState<string[]>(appliedAutoscaleState);
  const [pendingEligibility, setPendingEligibility] = useState<string[]>(appliedEligibility);
  const [pendingClusterNameFilters, setPendingClusterNameFilters] =
    useState<SelectOption[]>(appliedClusterNameFilters);
  const [pendingVolumeIdFilters, setPendingVolumeIdFilters] =
    useState<SelectOption[]>(appliedVolumeIdFilters);
  const [pendingVolumeTagFilters, setPendingVolumeTagFilters] =
    useState<SelectOption[]>(appliedVolumeTagFilters);
  const [pendingAccountFilters, setPendingAccountFilters] =
    useState<SelectOption[]>(appliedAccountFilters);
  const [pendingInstanceFilters, setPendingInstanceFilters] =
    useState<SelectOption[]>(appliedInstanceFilters);
  const [lastSyncedOpen, setLastSyncedOpen] = useState(open);

  useEffect(() => {
    if (open && !lastSyncedOpen) {
      setPendingAutoscaleState(appliedAutoscaleState);
      setPendingEligibility(appliedEligibility);
      setPendingClusterNameFilters(appliedClusterNameFilters);
      setPendingVolumeIdFilters(appliedVolumeIdFilters);
      setPendingVolumeTagFilters(appliedVolumeTagFilters);
      setPendingAccountFilters(appliedAccountFilters);
      setPendingInstanceFilters(appliedInstanceFilters);
    }
    setLastSyncedOpen(open);
  }, [
    open,
    lastSyncedOpen,
    appliedAutoscaleState,
    appliedEligibility,
    appliedClusterNameFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
    appliedInstanceFilters,
  ]);

  const resetPendingFilters = () => {
    setPendingAutoscaleState([]);
    setPendingEligibility([]);
    setPendingClusterNameFilters([]);
    setPendingVolumeIdFilters([]);
    setPendingVolumeTagFilters([]);
    setPendingAccountFilters([]);
    setPendingInstanceFilters([]);
  };

  const applyPendingFilters = () => {
    applyFilters(
      pendingAutoscaleState,
      pendingEligibility,
      pendingClusterNameFilters,
      pendingVolumeIdFilters,
      pendingVolumeTagFilters,
      pendingAccountFilters,
      pendingInstanceFilters
    );
  };

  return {
    pendingAutoscaleState,
    setPendingAutoscaleState,
    pendingEligibility,
    setPendingEligibility,
    pendingClusterNameFilters,
    setPendingClusterNameFilters,
    pendingVolumeIdFilters,
    setPendingVolumeIdFilters,
    pendingVolumeTagFilters,
    setPendingVolumeTagFilters,
    pendingAccountFilters,
    setPendingAccountFilters,
    pendingInstanceFilters,
    setPendingInstanceFilters,
    resetPendingFilters,
    applyPendingFilters,
  };
}
